import { DBCampaignLogThunks } from '@common-reducers/DBServiceThunks';
import { selectFilteredCampaignLogCampaignIds } from '@common-reducers/CampaignSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useState, useEffect } from 'react';
import { CampaignLogStatus } from '@bt-common/schemas/bt/campaign-log';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutGrid, List } from 'lucide-react';
import { CampaignGroupCard } from './components/CampaignGroupCard';
import { CampaignGroupTable } from './components/CampaignGroupTable';
import { CampaignLogFilters } from './components/CampaignLogFilters';

interface CampaignLogsTabProps {
    supportedUserId?: string;
    campaignId?: string;
}

export function CampaignLogsTab({ supportedUserId, campaignId }: CampaignLogsTabProps) {
    const [viewMode, setViewMode] = useState<'timeline' | 'list'>('list');
    
    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<CampaignLogStatus | 'all'>('all');
    const [sourceFilter, setSourceFilter] = useState<'backend' | 'client' | 'all'>('all');
    const [clientTypeFilter, setClientTypeFilter] = useState<'gateway' | 'whatsapp' | 'frontend' | 'all'>('all');

    const dispatch = useAppDispatch();

    // Load campaign logs
    useEffect(() => {
        if (supportedUserId) {
            const query: any = {
                userId: supportedUserId,
                $sort: { createdAt: -1 },
                $limit: -1
            };
            
            if (campaignId) {
                query.campaignId = campaignId;
            }

            dispatch(DBCampaignLogThunks.find(query));
        }
    }, [supportedUserId, campaignId, dispatch]);

    // Get filtered campaign IDs for both views
    const filteredCampaignIds = useAppSelector(state => 
        selectFilteredCampaignLogCampaignIds(
            state, 
            supportedUserId || '', 
            searchQuery, 
            statusFilter, 
            sourceFilter, 
            clientTypeFilter
        )
    );

    const handleClearFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
        setSourceFilter('all');
        setClientTypeFilter('all');
    };

    if (!supportedUserId) {
        return (
            <div className="flex items-center justify-center py-12 text-slate-500">
                No user selected
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full gap-4">
            {/* Filters */}
            <CampaignLogFilters
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                sourceFilter={sourceFilter}
                clientTypeFilter={clientTypeFilter}
                onSearchChange={setSearchQuery}
                onStatusFilterChange={setStatusFilter}
                onSourceFilterChange={setSourceFilter}
                onClientTypeFilterChange={setClientTypeFilter}
                onClearFilters={handleClearFilters}
            />

            {/* View Toggle */}
            <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                    Showing {filteredCampaignIds.length} campaign{filteredCampaignIds.length !== 1 ? 's' : ''}
                </div>
                <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'timeline' | 'list')}>
                    <TabsList>
                        <TabsTrigger value="timeline">
                            <LayoutGrid className="h-4 w-4 mr-2" />
                            Timeline
                        </TabsTrigger>
                        <TabsTrigger value="list">
                            <List className="h-4 w-4 mr-2" />
                            List
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {viewMode === 'timeline' ? (
                    <div className="space-y-4">
                        {filteredCampaignIds.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                No campaign logs found matching your filters
                            </div>
                        ) : (
                            filteredCampaignIds.map((cId) => (
                                <CampaignGroupCard
                                    key={cId}
                                    campaignId={cId}
                                />
                            ))
                        )}
                    </div>
                ) : (
                    <CampaignGroupTable
                        campaignIds={filteredCampaignIds}
                    />
                )}
            </div>
        </div>
    );
}

